import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { dossierApi, API_BASE, BACKEND_URL } from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import { Toaster } from "../../components/ui/toaster";
import {
  ArrowLeft, Check, RotateCcw, Copy, Upload, FileText, Trash2, Plus,
  CalendarClock, Send, Loader2, ExternalLink,
} from "lucide-react";

const fmtDateTime = (d) => (d ? new Date(d).toLocaleString("nl-BE", { dateStyle: "medium", timeStyle: "short" }) : "—");
// Convert a stored UTC ISO string to a value usable by <input type="datetime-local">
// (local wall-clock, YYYY-MM-DDTHH:mm). Fixes the tz round-trip: saving uses
// new Date(value).toISOString() (local→UTC), so display must convert UTC→local.
const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - off).toISOString().slice(0, 16);
};

export default function AdminDossierDetail() {
  const { id } = useParams();
  const { toast } = useToast();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const fileRef = useRef(null);

  const load = () => dossierApi.get(id).then(setD).finally(() => setLoading(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (loading) return <div className="p-8 text-white/50">Laden...</div>;
  if (!d) return <div className="p-8 text-white/50">Dossier niet gevonden.</div>;

  const stepObj = (key) => d.steps[key] || {};
  const isDone = (key) => stepObj(key).status === "completed";

  const toggleStep = async (key) => {
    setBusy(key);
    try {
      const res = isDone(key) ? await dossierApi.reopenStep(id, key) : await dossierApi.completeStep(id, key);
      setD(res);
      if (!isDone(key)) toast({ title: "Stap voltooid", description: "Klant kreeg een melding." });
    } catch (e) {
      toast({ title: "Fout", description: e?.response?.data?.detail || String(e) });
    } finally {
      setBusy(null);
    }
  };

  const patch = async (body, okMsg) => {
    try {
      const res = await dossierApi.patch(id, body);
      setD(res);
      if (okMsg) toast({ title: okMsg });
    } catch (e) {
      toast({ title: "Opslaan mislukt", description: e?.response?.data?.detail || String(e) });
    }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(d.share_url); toast({ title: "Link gekopieerd" }); } catch {}
  };

  const uploadDocs = async (files) => {
    const list = Array.from(files);
    for (const file of list) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        await axios.post(`${API_BASE}/admin/dossiers/${id}/documents`, fd, {
          withCredentials: true, headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
        });
      } catch (e) {
        toast({ title: "Upload mislukt", description: e?.response?.data?.detail || file.name });
      }
    }
    load();
  };

  const removeDoc = async (docId) => {
    await dossierApi.removeDoc(id, docId);
    load();
  };

  return (
    <div className="p-6 md:p-8 max-w-4xl" data-testid="admin-dossier-detail">
      <Toaster />
      <Link to="/admin/dossiers" className="inline-flex items-center gap-2 text-white/60 hover:text-[#c5a76f] text-sm mb-5">
        <ArrowLeft className="w-4 h-4" /> Alle dossiers
      </Link>

      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: "'Familjen Grotesk', serif" }}>
            {d.car_title}
          </h1>
          <p className="text-white/50 text-sm mt-1">
            {d.progress.completed}/{d.progress.total} stappen voltooid
          </p>
        </div>
        <label className="flex items-center gap-2 bg-[#242424] border border-white/10 rounded px-3 py-2">
          <span className="text-white/50 text-xs">Taal klant</span>
          <select
            value={d.language || "nl"}
            onChange={(e) => patch({ language: e.target.value }, "Taal aangepast")}
            data-testid="dossier-language-select"
            className="bg-[#1a1a1a] border border-white/10 rounded px-2 py-1 text-sm text-white focus:outline-none"
          >
            <option value="nl">NL</option>
            <option value="fr">FR</option>
            <option value="en">EN</option>
          </select>
        </label>
      </div>

      {/* Share link */}
      <div className="bg-[#242424] border border-white/10 rounded-md p-4 mb-6">
        <div className="text-white/50 text-xs uppercase tracking-wider mb-2">Opvolglink voor de klant</div>
        <div className="flex items-center gap-2">
          <input readOnly value={d.share_url} data-testid="dossier-share-url"
            className="flex-1 bg-[#1a1a1a] border border-white/10 rounded px-3 py-2 text-[#c5a76f] text-xs font-mono" />
          <button onClick={copyLink} data-testid="dossier-copy-link" className="px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded text-white text-xs">
            <Copy className="w-3.5 h-3.5" />
          </button>
          <a href={d.share_url} target="_blank" rel="noreferrer" className="px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded text-white text-xs">
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>

      {/* Contacts */}
      <ContactsBlock d={d} onSave={patch} />

      {/* Steps */}
      <div className="mt-6 space-y-3">
        <h2 className="text-white/80 text-sm uppercase tracking-wider">Stappen</h2>
        {d.steps && [
          ["bestelbon", "Bestelbon getekend"],
          ["voorschot", "Voorschot ontvangen"],
          ["keuring_planning", "Keuring ingepland"],
          ["keuring_voltooid", "Keuring voltooid"],
          ["saldo", "Saldobetaling ontvangen"],
          ["extra_werken", "Extra werken"],
          ["aflevermoment", "Aflevermoment ingepland"],
          ["afgeleverd", "Afgeleverd"],
        ].map(([key, label], idx) => (
          <div key={key} className={`bg-[#242424] border rounded-md p-4 ${isDone(key) ? "border-emerald-500/20" : "border-white/8"}`} data-testid={`step-${key}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className={`w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-xs font-bold ${isDone(key) ? "bg-emerald-500 text-black" : "bg-white/10 text-white/60"}`}>
                  {isDone(key) ? <Check className="w-4 h-4" /> : idx + 1}
                </span>
                <div>
                  <div className="text-white text-sm font-medium">{label}</div>
                  {isDone(key) && <div className="text-emerald-400/70 text-xs">Voltooid op {fmtDateTime(stepObj(key).completed_at)}</div>}
                </div>
              </div>
              <button
                onClick={() => toggleStep(key)}
                disabled={busy === key}
                data-testid={`step-toggle-${key}`}
                className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded transition-colors disabled:opacity-50 ${
                  isDone(key) ? "text-white/50 hover:text-white border border-white/10" : "bg-[#c5a76f] text-black hover:bg-[#d8bd86] font-medium"
                }`}
              >
                {busy === key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : isDone(key) ? <RotateCcw className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5" />}
                {isDone(key) ? "Heropen" : "Voltooien"}
              </button>
            </div>

            {/* Step-specific controls */}
            {key === "keuring_planning" && (
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 pl-10">
                <label className="text-xs text-white/50">Van
                  <input type="date" defaultValue={stepObj(key).date_from || ""} data-testid="keuring-date-from"
                    onBlur={(e) => patch({ keuring_date_from: e.target.value || null })}
                    className="mt-1 w-full bg-[#1a1a1a] border border-white/10 rounded px-3 py-2 text-sm text-white" />
                </label>
                <label className="text-xs text-white/50">Tot (optioneel)
                  <input type="date" defaultValue={stepObj(key).date_to || ""} data-testid="keuring-date-to"
                    onBlur={(e) => patch({ keuring_date_to: e.target.value || null })}
                    className="mt-1 w-full bg-[#1a1a1a] border border-white/10 rounded px-3 py-2 text-sm text-white" />
                </label>
              </div>
            )}

            {key === "keuring_voltooid" && (
              <div className="mt-3 pl-10 space-y-2">
                {(stepObj(key).documents || []).map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between gap-2 bg-[#1a1a1a] border border-white/10 rounded px-3 py-2">
                    <a href={`${BACKEND_URL}${doc.url}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-[#c5a76f] hover:underline truncate">
                      <FileText className="w-4 h-4 shrink-0" /> <span className="truncate">{doc.name}</span>
                    </a>
                    <button onClick={() => removeDoc(doc.id)} data-testid={`doc-delete-${doc.id}`} className="text-white/40 hover:text-red-400 shrink-0">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <input ref={fileRef} type="file" multiple accept=".pdf,image/*" className="hidden" onChange={(e) => uploadDocs(e.target.files)} data-testid="doc-upload-input" />
                <button onClick={() => fileRef.current?.click()} data-testid="doc-upload-btn"
                  className="flex items-center gap-2 text-xs text-white/70 hover:text-[#c5a76f] border border-dashed border-white/15 rounded px-3 py-2 w-full justify-center">
                  <Upload className="w-3.5 h-3.5" /> Keuringsdocument(en) uploaden (1-2)
                </button>
                <RegistrationAdmin dossierId={id} step={stepObj(key)} onPatch={patch} onReload={load} />
              </div>
            )}

            {key === "extra_werken" && <ExtraWerken dossierId={id} items={stepObj(key).items || []} onChange={load} />}

            {key === "aflevermoment" && (
              <div className="mt-3 pl-10">
                <label className="text-xs text-white/50">Datum &amp; tijdstip (30 min in agenda)
                  <input type="datetime-local" defaultValue={toLocalInput(stepObj(key).scheduled_at)} data-testid="aflever-datetime"
                    onBlur={(e) => patch({ aflevermoment_scheduled_at: e.target.value ? new Date(e.target.value).toISOString() : null }, "Aflevermoment opgeslagen")}
                    className="mt-1 w-full max-w-xs bg-[#1a1a1a] border border-white/10 rounded px-3 py-2 text-sm text-white" />
                </label>
                <p className="text-white/40 text-xs mt-2 flex items-center gap-1.5">
                  <CalendarClock className="w-3.5 h-3.5" />
                  Bij "Voltooien" wordt de klant + bank/verzekering verwittigd en (indien gekoppeld) een agenda-event aangemaakt.
                </p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Comments */}
      <CommentsBlock dossierId={id} comments={d.comments || []} onChange={load} />
    </div>
  );
}

function ContactsBlock({ d, onSave }) {
  const [c, setC] = useState(d.customer || {});
  const [bank, setBank] = useState(d.bank || {});
  const [ins, setIns] = useState(d.insurance || {});

  const Row = ({ title, val, set, testPrefix }) => (
    <div className="bg-[#242424] border border-white/8 rounded-md p-4">
      <div className="text-white/50 text-xs uppercase tracking-wider mb-2">{title}</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <input placeholder="Naam" defaultValue={val.name || ""} data-testid={`${testPrefix}-name`} onChange={(e) => set({ ...val, name: e.target.value })}
          className="bg-[#1a1a1a] border border-white/10 rounded px-3 py-2 text-sm text-white" />
        <input placeholder="E-mail" defaultValue={val.email || ""} data-testid={`${testPrefix}-email`} onChange={(e) => set({ ...val, email: e.target.value })}
          className="bg-[#1a1a1a] border border-white/10 rounded px-3 py-2 text-sm text-white" />
        <input placeholder="Telefoon" defaultValue={val.phone || ""} data-testid={`${testPrefix}-phone`} onChange={(e) => set({ ...val, phone: e.target.value })}
          className="bg-[#1a1a1a] border border-white/10 rounded px-3 py-2 text-sm text-white" />
      </div>
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-white/80 text-sm uppercase tracking-wider">Contactgegevens</h2>
        <button
          onClick={() => onSave({ customer: c, bank: bank.name || bank.email || bank.phone ? bank : { name: null, email: null, phone: null }, insurance: ins.name || ins.email || ins.phone ? ins : { name: null, email: null, phone: null } }, "Gegevens opgeslagen")}
          data-testid="contacts-save"
          className="text-xs px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded text-white">
          Opslaan
        </button>
      </div>
      <Row title="Klant" val={c} set={setC} testPrefix="contact-customer" />
      <Row title="Bank" val={bank} set={setBank} testPrefix="contact-bank" />
      <Row title="Verzekering" val={ins} set={setIns} testPrefix="contact-insurance" />
    </div>
  );
}

function RegistrationAdmin({ dossierId, step, onPatch, onReload }) {
  const { toast } = useToast();
  const [sending, setSending] = useState(false);
  const type = step.registration_type || "";
  const choice = step.registration_choice || "";
  const OPTS = [
    "Vignet + info voor roze formulier naar ons (Wichelsesteenweg 100, 9340 Lede) — wij dienen persoonlijk in bij DIV Gent (snelste & veiligste optie)",
    "Documenten aangetekend opsturen naar klant",
    "Documenten aangetekend opsturen naar verzekeringsmakelaar",
    "Klant komt documenten ophalen bij ons op de zaak",
  ];

  const sendInfo = async () => {
    setSending(true);
    try {
      const res = await dossierApi.sendRegistrationInfo(dossierId);
      toast({ title: "Registratie-info verstuurd", description: `Naar: ${(res.sent || []).join(", ") || "geen ontvangers"}` });
    } catch (e) {
      toast({ title: "Niet verstuurd", description: e?.response?.data?.detail || String(e) });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-4 border-t border-white/8 pt-3" data-testid="registration-admin">
      <div className="text-white/60 text-xs uppercase tracking-wider mb-2">Registratie (DIV)</div>
      <div className="flex gap-2 mb-3">
        {[["", "Geen"], ["online", "Online (WEBDIV)"], ["manueel", "Manueel (DIV)"]].map(([v, lbl]) => (
          <button
            key={v || "none"}
            onClick={() => onPatch({ registration_type: v || null }, "Registratietype opgeslagen")}
            data-testid={`reg-type-${v || "none"}`}
            className={`text-xs px-3 py-1.5 rounded border transition-colors ${
              type === v ? "bg-[#c5a76f] text-black border-[#c5a76f] font-medium" : "border-white/15 text-white/70 hover:bg-white/5"
            }`}
          >
            {lbl}
          </button>
        ))}
      </div>

      {step.plate_combination && (
        <div className="mb-3 bg-[#c5a76f]/10 border border-[#c5a76f]/40 rounded px-3 py-2.5" data-testid="reg-plate-combination">
          <div className="text-[#c5a76f] text-[10px] uppercase tracking-wider mb-0.5">Nummerplaatcombinatie (doorgegeven)</div>
          <div className="text-white text-base font-bold tracking-widest">{step.plate_combination}</div>
        </div>
      )}

      {type === "online" && (
        <p className="text-xs text-white/55 bg-[#1a1a1a] border border-white/10 rounded p-3">
          Het roze formulier is beschikbaar voor online registratie via WEBDIV. Klant + makelaar krijgen deze uitleg.
        </p>
      )}

      {type === "manueel" && (
        <div className="space-y-1.5">
          <p className="text-xs text-white/55 mb-1">Klant/makelaar kiest één optie (jij kan ook vooraf kiezen):</p>
          {OPTS.map((o, i) => {
            const n = String(i + 1);
            return (
              <button
                key={n}
                onClick={() => onPatch({ registration_choice: choice === n ? null : n }, "Keuze opgeslagen")}
                data-testid={`reg-opt-${n}`}
                className={`w-full text-left text-xs px-3 py-2 rounded border flex gap-2 transition-colors ${
                  choice === n ? "bg-[#c5a76f]/15 border-[#c5a76f]/40 text-white" : "border-white/10 text-white/65 hover:bg-white/5"
                }`}
              >
                <span className={`shrink-0 font-bold ${choice === n ? "text-[#c5a76f]" : "text-white/40"}`}>{choice === n ? "✓" : n}</span>
                <span>{o}</span>
              </button>
            );
          })}
        </div>
      )}

      {type && (
        <button onClick={sendInfo} disabled={sending} data-testid="reg-send-info"
          className="mt-3 flex items-center gap-2 text-xs font-medium text-black bg-[#c5a76f] hover:bg-[#d8bd86] rounded px-3 py-2 disabled:opacity-50">
          {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          Registratie-info versturen naar klant &amp; verzekering
        </button>
      )}
    </div>
  );
}

function ExtraWerken({ dossierId, items, onChange }) {
  const [text, setText] = useState("");
  const add = async () => {
    if (!text.trim()) return;
    await dossierApi.addExtra(dossierId, { description: text.trim(), status: "pending" });
    setText("");
    onChange();
  };
  const toggle = async (it) => {
    await dossierApi.patchExtra(dossierId, it.id, { description: it.description, status: it.status === "done" ? "pending" : "done" });
    onChange();
  };
  const del = async (it) => { await dossierApi.removeExtra(dossierId, it.id); onChange(); };

  return (
    <div className="mt-3 pl-10 space-y-2">
      {items.map((it) => (
        <div key={it.id} className="flex items-center gap-2 bg-[#1a1a1a] border border-white/10 rounded px-3 py-2" data-testid={`extra-item-${it.id}`}>
          <button onClick={() => toggle(it)} className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 ${it.status === "done" ? "bg-emerald-500 border-emerald-500" : "border-white/30"}`}>
            {it.status === "done" && <Check className="w-3 h-3 text-black" />}
          </button>
          <span className={`text-sm flex-1 ${it.status === "done" ? "text-white/40 line-through" : "text-white/85"}`}>{it.description}</span>
          <button onClick={() => del(it)} className="text-white/40 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      <div className="flex gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Bv. onderhoud, 4 nieuwe banden, carrosserie..." data-testid="extra-input"
          className="flex-1 bg-[#1a1a1a] border border-white/10 rounded px-3 py-2 text-sm text-white placeholder:text-white/25" />
        <button onClick={add} data-testid="extra-add" className="px-3 rounded bg-[#c5a76f] text-black"><Plus className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

function CommentsBlock({ dossierId, comments, onChange }) {
  const { toast } = useToast();
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await dossierApi.addAdminComment(dossierId, { text: text.trim() });
      setText("");
      onChange();
      toast({ title: "Bericht verstuurd", description: "Klant kreeg een melding." });
    } catch (e) {
      toast({ title: "Fout", description: e?.response?.data?.detail || String(e) });
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="mt-8">
      <h2 className="text-white/80 text-sm uppercase tracking-wider mb-3">Berichten met de klant</h2>
      <div className="space-y-2 mb-3">
        {comments.length === 0 && <div className="text-white/40 text-sm">Nog geen berichten.</div>}
        {comments.map((cm) => (
          <div key={cm.id} className={`rounded-md p-3 text-sm ${cm.author === "admin" ? "bg-[#c5a76f]/10 border border-[#c5a76f]/20" : "bg-[#242424] border border-white/8"}`}>
            <div className="flex items-center justify-between text-xs text-white/40 mb-1">
              <span className={cm.author === "admin" ? "text-[#c5a76f]" : "text-white/70"}>{cm.name}</span>
              <span>{fmtDateTime(cm.created_at)}</span>
            </div>
            <div className="text-white/85 whitespace-pre-wrap">{cm.text}</div>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Bericht aan de klant..."
          data-testid="admin-comment-input"
          className="flex-1 bg-[#1a1a1a] border border-white/10 rounded px-3 py-2 text-sm text-white placeholder:text-white/25 resize-none" />
        <button onClick={send} disabled={sending} data-testid="admin-comment-send"
          className="px-4 rounded bg-[#c5a76f] text-black font-medium disabled:opacity-50 flex items-center gap-2">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}
